import { useState, useEffect, useRef, useContext, useCallback } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { 
  Citrus, Coins, Trophy, Timer, Waves, Target, Zap,
  Play, RotateCcw, Volume2, VolumeX
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const CoinLemonPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  
  const [betAmount, setBetAmount] = useState(10);
  const [difficulty, setDifficulty] = useState("medium");
  const [gameState, setGameState] = useState("idle"); // idle, playing, won, lost
  const [balanceDuration, setBalanceDuration] = useState(0);
  const [lastResult, setLastResult] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Physics state
  const [coinAngle, setCoinAngle] = useState(0);
  const [lemonWobble, setLemonWobble] = useState(0);
  const [waterOffset, setWaterOffset] = useState(0);
  
  const difficultySettings = {
    easy: { sensitivity: 0.3, wobbleSpeed: 0.5, multiplierBonus: 1.0, label: "Easy", color: "#00E701" },
    medium: { sensitivity: 0.5, wobbleSpeed: 1.0, multiplierBonus: 1.5, label: "Medium", color: "#FFD600" },
    hard: { sensitivity: 0.8, wobbleSpeed: 1.5, multiplierBonus: 2.5, label: "Hard", color: "#FF3B30" }
  };
  
  // Calculate potential multiplier
  const getPotentialMultiplier = (duration, diff) => {
    const base = difficultySettings[diff].multiplierBonus;
    if (duration < 1) return 0;
    return Math.min(base * (1 + Math.pow(duration, 1.3) * 0.2), 25);
  };
  
  // Draw game canvas
  const drawGame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear canvas
    ctx.fillStyle = "#0A0A0B";
    ctx.fillRect(0, 0, width, height);
    
    // Draw water waves
    ctx.fillStyle = "#1a4a6e";
    ctx.beginPath();
    ctx.moveTo(0, height * 0.65);
    for (let x = 0; x <= width; x += 10) {
      const y = height * 0.65 + Math.sin((x + waterOffset) * 0.02) * 8 + Math.sin((x + waterOffset * 1.5) * 0.03) * 5;
      ctx.lineTo(x, y);
    }
    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.fill();
    
    // Draw deeper water
    ctx.fillStyle = "#0f3854";
    ctx.beginPath();
    ctx.moveTo(0, height * 0.75);
    for (let x = 0; x <= width; x += 10) {
      const y = height * 0.75 + Math.sin((x + waterOffset * 0.8) * 0.025) * 5;
      ctx.lineTo(x, y);
    }
    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.fill();
    
    // Draw bowl/container
    ctx.strokeStyle = "#3a3a4a";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.ellipse(width / 2, height * 0.8, width * 0.35, height * 0.15, 0, 0, Math.PI);
    ctx.stroke();
    
    // Lemon position (floating and wobbling)
    const lemonX = width / 2 + Math.sin(lemonWobble * 0.5) * 15;
    const lemonY = height * 0.55 + Math.sin(lemonWobble) * 5;
    const lemonRotation = Math.sin(lemonWobble * 0.7) * 0.1;
    
    // Draw lemon
    ctx.save();
    ctx.translate(lemonX, lemonY);
    ctx.rotate(lemonRotation);
    
    // Lemon body
    const gradient = ctx.createRadialGradient(0, 0, 10, 0, 0, 45);
    gradient.addColorStop(0, "#FFE066");
    gradient.addColorStop(0.7, "#FFD600");
    gradient.addColorStop(1, "#E6B800");
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.ellipse(0, 0, 50, 35, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Lemon highlights
    ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
    ctx.beginPath();
    ctx.ellipse(-15, -10, 15, 8, -0.3, 0, Math.PI * 2);
    ctx.fill();
    
    // Lemon tips
    ctx.fillStyle = "#B8A000";
    ctx.beginPath();
    ctx.ellipse(-48, 0, 8, 6, 0.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(48, 0, 8, 6, -0.2, 0, Math.PI * 2);
    ctx.fill();
    
    // Draw coin on top of lemon
    if (gameState === "playing" || gameState === "won") {
      const coinY = -45 + (gameState === "won" ? 0 : Math.abs(coinAngle) * 2);
      
      ctx.save();
      ctx.rotate(coinAngle);
      ctx.translate(0, coinY);
      
      // Coin shadow
      ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
      ctx.beginPath();
      ctx.ellipse(3, 3, 22, 22, 0, 0, Math.PI * 2);
      ctx.fill();
      
      // Coin body
      const coinGradient = ctx.createRadialGradient(-5, -5, 5, 0, 0, 22);
      coinGradient.addColorStop(0, "#FFD700");
      coinGradient.addColorStop(0.5, "#FFC000");
      coinGradient.addColorStop(1, "#B8860B");
      ctx.fillStyle = coinGradient;
      ctx.beginPath();
      ctx.arc(0, 0, 22, 0, Math.PI * 2);
      ctx.fill();
      
      // Coin edge
      ctx.strokeStyle = "#8B7500";
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Coin symbol (₹)
      ctx.fillStyle = "#8B6914";
      ctx.font = "bold 20px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("₹", 0, 0);
      
      ctx.restore();
    }
    
    // Coin falling animation when lost
    if (gameState === "lost") {
      const fallProgress = (Date.now() % 1000) / 1000;
      const fallY = -45 + fallProgress * 150;
      const fallX = Math.sin(fallProgress * Math.PI * 2) * 30;
      
      ctx.save();
      ctx.translate(fallX, fallY);
      ctx.rotate(fallProgress * Math.PI * 4);
      
      ctx.fillStyle = "#FFC000";
      ctx.beginPath();
      ctx.arc(0, 0, 18, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.restore();
    }
    
    ctx.restore();
    
    // Draw timer/duration
    if (gameState === "playing") {
      ctx.fillStyle = "#00E701";
      ctx.font = "bold 48px Rajdhani";
      ctx.textAlign = "center";
      ctx.fillText(`${balanceDuration.toFixed(1)}s`, width / 2, 60);
      
      // Potential multiplier
      const potMult = getPotentialMultiplier(balanceDuration, difficulty);
      ctx.fillStyle = "#FFD600";
      ctx.font = "bold 24px Rajdhani";
      ctx.fillText(`${potMult.toFixed(2)}x`, width / 2, 95);
    }
    
    // Win display
    if (gameState === "won" && lastResult) {
      ctx.fillStyle = "#00E701";
      ctx.font = "bold 36px Rajdhani";
      ctx.textAlign = "center";
      ctx.fillText("BALANCED!", width / 2, 50);
      ctx.fillText(`+₹${lastResult.winnings.toFixed(0)}`, width / 2, 90);
    }
    
    // Loss display
    if (gameState === "lost") {
      ctx.fillStyle = "#FF3B30";
      ctx.font = "bold 36px Rajdhani";
      ctx.textAlign = "center";
      ctx.fillText("DROPPED!", width / 2, 60);
    }
  }, [gameState, coinAngle, lemonWobble, waterOffset, balanceDuration, difficulty, lastResult]);
  
  // Game animation loop
  useEffect(() => {
    let lastTime = Date.now();
    let accumulatedTime = 0;
    
    const animate = () => {
      const now = Date.now();
      const delta = (now - lastTime) / 1000;
      lastTime = now;
      
      // Update water animation
      setWaterOffset(prev => prev + delta * 50);
      
      // Update lemon wobble
      const settings = difficultySettings[difficulty];
      setLemonWobble(prev => prev + delta * settings.wobbleSpeed * 3);
      
      if (gameState === "playing") {
        accumulatedTime += delta;
        setBalanceDuration(accumulatedTime);
        
        // Random coin tilting based on difficulty
        setCoinAngle(prev => {
          const wobbleEffect = Math.sin(now * 0.005) * settings.sensitivity * 0.3;
          const randomTilt = (Math.random() - 0.5) * settings.sensitivity * 0.1;
          return prev * 0.95 + wobbleEffect + randomTilt;
        });
      }
      
      drawGame();
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameState, difficulty, drawGame]);
  
  // Mouse/touch control for balancing
  useEffect(() => {
    if (gameState !== "playing") return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const handleMove = (clientX) => {
      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const centerX = rect.width / 2;
      const offset = (x - centerX) / centerX;
      
      setCoinAngle(prev => {
        const target = offset * 0.5;
        return prev + (target - prev) * 0.1;
      });
    };
    
    const onMouseMove = (e) => handleMove(e.clientX);
    const onTouchMove = (e) => {
      e.preventDefault();
      handleMove(e.touches[0].clientX);
    };
    
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    
    return () => {
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("touchmove", onTouchMove);
    };
  }, [gameState]);
  
  const startGame = () => {
    if (betAmount <= 0 || betAmount > (user?.balance || 0)) {
      toast.error("Invalid bet amount");
      return;
    }
    
    setGameState("playing");
    setBalanceDuration(0);
    setCoinAngle(0);
    setLastResult(null);
    
    if (soundEnabled) {
      // Play start sound (would need actual audio files)
    }
  };
  
  const cashOut = async () => {
    if (gameState !== "playing") return;
    
    setGameState("processing");
    
    try {
      const response = await axios.post(`${API}/games/coinlemon/play`, {
        game_type: "coinlemon",
        bet_amount: betAmount,
        bet_data: {
          balance_duration: balanceDuration,
          difficulty: difficulty
        }
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLastResult(response.data);
      setGameState(response.data.won ? "won" : "lost");
      
      if (response.data.won) {
        toast.success(`Won ₹${response.data.winnings.toFixed(0)} at ${response.data.multiplier.toFixed(2)}x!`);
      }
      
      await refreshBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Game error");
      setGameState("idle");
    }
  };
  
  const dropCoin = async () => {
    if (gameState !== "playing") return;
    
    // Coin fell - lost the bet
    setGameState("processing");
    
    try {
      const response = await axios.post(`${API}/games/coinlemon/play`, {
        game_type: "coinlemon",
        bet_amount: betAmount,
        bet_data: {
          balance_duration: 0,
          difficulty: difficulty
        }
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLastResult(response.data);
      setGameState("lost");
      toast.error("The coin dropped! Better luck next time.");
      
      await refreshBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Game error");
      setGameState("idle");
    }
  };
  
  const resetGame = () => {
    setGameState("idle");
    setBalanceDuration(0);
    setCoinAngle(0);
    setLastResult(null);
  };
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FFD600] to-[#FF6B00] flex items-center justify-center">
              <Citrus className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Rajdhani']">Coin on Lemon</h1>
              <p className="text-[#A1A1AA] text-sm">Balance a coin on a lemon floating in water</p>
            </div>
          </div>
          
          <button 
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="p-2 rounded-lg bg-white/5 text-white/60 hover:text-white"
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Game Canvas */}
          <div className="lg:col-span-2 p-4 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
            <canvas 
              ref={canvasRef}
              width={600}
              height={400}
              className="w-full rounded-lg bg-[#0A0A0B] cursor-crosshair"
              data-testid="coinlemon-canvas"
            />
            
            {/* Game controls overlay */}
            <div className="mt-4 flex justify-center gap-4">
              {gameState === "idle" && (
                <Button
                  onClick={startGame}
                  disabled={betAmount <= 0 || betAmount > (user?.balance || 0)}
                  className="px-8 h-12 bg-[#00E701] hover:bg-[#00C201] text-black font-bold text-lg"
                  data-testid="start-game-btn"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Place Coin (₹{betAmount})
                </Button>
              )}
              
              {gameState === "playing" && (
                <>
                  <Button
                    onClick={cashOut}
                    className="px-6 h-12 bg-[#FFD600] hover:bg-[#E6C200] text-black font-bold"
                    data-testid="cashout-btn"
                  >
                    <Trophy className="w-5 h-5 mr-2" />
                    Cash Out ({getPotentialMultiplier(balanceDuration, difficulty).toFixed(2)}x)
                  </Button>
                  <Button
                    onClick={dropCoin}
                    variant="outline"
                    className="px-6 h-12 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                  >
                    Drop Coin
                  </Button>
                </>
              )}
              
              {(gameState === "won" || gameState === "lost") && (
                <Button
                  onClick={resetGame}
                  className="px-8 h-12 bg-[#007AFF] hover:bg-[#0066DD] text-white font-bold"
                  data-testid="reset-btn"
                >
                  <RotateCcw className="w-5 h-5 mr-2" />
                  Play Again
                </Button>
              )}
            </div>
            
            {/* Instructions */}
            <div className="mt-4 p-3 rounded-lg bg-white/5 text-sm text-[#A1A1AA]">
              <p className="flex items-center gap-2">
                <Target className="w-4 h-4 text-[#FFD600]" />
                <span>Move your mouse/finger to balance the coin. Longer balance = higher multiplier!</span>
              </p>
            </div>
          </div>
          
          {/* Controls Panel */}
          <div className="space-y-4">
            {/* Bet Amount */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <label className="text-[#A1A1AA] text-sm mb-2 block">Bet Amount</label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={betAmount}
                  onChange={(e) => setBetAmount(Number(e.target.value))}
                  disabled={gameState === "playing"}
                  className="h-12 bg-black/20 border-white/10 text-white font-mono text-lg"
                  data-testid="bet-input"
                />
              </div>
              <div className="flex gap-2 mt-2">
                {[10, 50, 100, 500].map(amt => (
                  <Button
                    key={amt}
                    variant="outline"
                    size="sm"
                    onClick={() => setBetAmount(amt)}
                    disabled={gameState === "playing"}
                    className="flex-1 border-white/10 text-white/60 hover:text-white text-xs"
                  >
                    ₹{amt}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Difficulty */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <label className="text-[#A1A1AA] text-sm mb-3 block">Difficulty</label>
              <div className="space-y-2">
                {Object.entries(difficultySettings).map(([key, settings]) => (
                  <button
                    key={key}
                    onClick={() => setDifficulty(key)}
                    disabled={gameState === "playing"}
                    className={`w-full p-3 rounded-lg border transition-all flex items-center justify-between ${
                      difficulty === key 
                        ? `border-[${settings.color}] bg-[${settings.color}]/10` 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    style={{ 
                      borderColor: difficulty === key ? settings.color : undefined,
                      backgroundColor: difficulty === key ? `${settings.color}15` : undefined
                    }}
                    data-testid={`difficulty-${key}`}
                  >
                    <span className="text-white font-medium">{settings.label}</span>
                    <span style={{ color: settings.color }} className="font-bold">
                      {settings.multiplierBonus}x base
                    </span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Multiplier Info */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-[#FFD600]/20 to-transparent border border-[#FFD600]/20">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-[#FFD600]" />
                <span className="text-white font-bold">Multiplier Guide</span>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-[#A1A1AA]">
                  <span>1 second</span>
                  <span className="text-[#00E701]">{getPotentialMultiplier(1, difficulty).toFixed(2)}x</span>
                </div>
                <div className="flex justify-between text-[#A1A1AA]">
                  <span>3 seconds</span>
                  <span className="text-[#FFD600]">{getPotentialMultiplier(3, difficulty).toFixed(2)}x</span>
                </div>
                <div className="flex justify-between text-[#A1A1AA]">
                  <span>5 seconds</span>
                  <span className="text-[#FF6B00]">{getPotentialMultiplier(5, difficulty).toFixed(2)}x</span>
                </div>
                <div className="flex justify-between text-[#A1A1AA]">
                  <span>10 seconds</span>
                  <span className="text-[#FF3B30]">{getPotentialMultiplier(10, difficulty).toFixed(2)}x</span>
                </div>
              </div>
            </div>
            
            {/* Last Result */}
            {lastResult && (
              <div className={`p-4 rounded-xl border ${
                lastResult.won 
                  ? 'bg-[#00E701]/10 border-[#00E701]/30' 
                  : 'bg-[#FF3B30]/10 border-[#FF3B30]/30'
              }`}>
                <div className="text-center">
                  <p className={`text-2xl font-bold font-mono ${lastResult.won ? 'text-[#00E701]' : 'text-[#FF3B30]'}`}>
                    {lastResult.won ? `+₹${lastResult.winnings.toFixed(0)}` : `-₹${betAmount}`}
                  </p>
                  <p className="text-[#A1A1AA] text-sm mt-1">
                    {lastResult.won 
                      ? `${lastResult.balance_duration.toFixed(1)}s @ ${lastResult.multiplier.toFixed(2)}x`
                      : 'Coin dropped!'
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
